<script setup>
/**
 * Patch Smiller: painel do Alceu como COLUNA ao lado das mensagens.
 *
 * Por que existe: o Dashboard App nativo é renderizado como aba mutuamente
 * exclusiva com a conversa (ver ConversationBox.vue — `v-show="!activeIndex"`
 * pras mensagens vs `v-show="activeIndex - 1 === index"` pro app). Ou seja, abrir
 * o Alceu escondia o histórico com o dentista, que é justamente o que o CX
 * precisa ler enquanto pergunta. Não há configuração que mude isso.
 *
 * A solução copia o padrão do painel do Copilot (CopilotContainer.vue): uma
 * coluna irmã, mesmas classes de layout, alternada pelo SidepanelSwitch. Aquele
 * layout é MIT, mas o Copilot em si é pago (backend em enterprise/:
 * copilot_threads_controller, copilot_message.rb) e fica intocado — aqui só
 * reusamos o padrão de coluna apontando pro NOSSO iframe. Também não dependemos
 * da flag `captain`, que o Internal::ReconcilePlanConfigService desligaria
 * diariamente no self-hosted community.
 *
 * O conteúdo é o mesmo Dashboard App já cadastrado em Configurações →
 * Integrações → Aplicativos do painel, reusando o DashboardAppFrame nativo — o
 * contrato do appContext (postMessage) fica idêntico ao da aba, sem duplicar
 * código.
 */
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import DashboardAppFrame from 'dashboard/components/widgets/DashboardApp/Frame.vue';

// Qual Dashboard App vai na coluna: o que tiver "alceu" no título (cadastrado
// pela UI, então dá pra renomear sem deploy). Se nenhum casar e existir só UM
// app na conta, usa ele — evita o painel nascer vazio por causa do nome. Com 2+
// apps e nenhum "alceu", não adivinha (mostraria o app errado).
const ALCEU_APP_TITLE = 'alceu';

// O Frame monta ids de DOM como `dashboard-app--frame-<position>-<index>` e
// resolve o iframe por getElementById. Como o MESMO app também é renderizado na
// aba nativa (com position 0..n-1), usar o mesmo número faria o getElementById
// casar com o iframe errado e o appContext ir pro frame da aba. Offset alto
// garante id único entre a aba e esta coluna.
const FRAME_POSITION = 90;

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentChat = useMapGetter('getSelectedChat');
const dashboardApps = useMapGetter('dashboardApps/getRecords');

const alceuApp = computed(() => {
  const apps = dashboardApps.value || [];
  const porTitulo = apps.find(app =>
    app.title?.toLowerCase().includes(ALCEU_APP_TITLE)
  );
  if (porTitulo) return porTitulo;
  return apps.length === 1 ? apps[0] : null;
});

// Sem conversa aberta o painel não tem contexto pra entregar (o iframe ficaria
// em "aguardando conversa"), então só aparece com chat selecionado.
const shouldShowAlceuPanel = computed(
  () =>
    !!uiSettings.value.is_alceu_panel_open &&
    !!currentChat.value?.id &&
    !!alceuApp.value
);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

// Em tela pequena a coluna vira overlay em cima da conversa — clicar fora fecha,
// igual ao painel de contato e ao do Copilot.
const closeAlceuPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_alceu_panel_open) {
    updateUISettings({ is_alceu_panel_open: false });
  }
};
</script>

<template>
  <div
    v-if="shouldShowAlceuPanel"
    v-on-click-outside="() => closeAlceuPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[380px] md:min-w-[380px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[420px] 2xl:w-[420px] shadow-lg md:shadow-none"
  >
    <!-- flex-1/min-h-0 garante altura pro iframe (que usa height:100%), mesmo
         padrão do wrapper do ContactPanel em ConversationSidebar.vue -->
    <div class="flex flex-1 min-h-0 overflow-hidden">
      <!-- Sem :key por conversa (a aba usa `currentChat.id + app.id`) de
           propósito: a página do Alceu já detecta a troca de ticket pelo
           appContext e reseta o próprio estado, então keyar só recarregaria o
           iframe e refaria as chamadas de API a cada conversa. -->
      <DashboardAppFrame
        :config="alceuApp.content"
        :current-chat="currentChat"
        :is-visible="shouldShowAlceuPanel"
        :position="FRAME_POSITION"
      />
    </div>
  </div>
  <template v-else />
</template>
